import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Star, ShieldCheck, Sparkles } from "lucide-react";
import { CLINIC, whatsappLink } from "../lib/clinic";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden linen-grain"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-overline mb-6"
          >
            The bruxism clinic · Acton, West London
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-serif-display text-rb-text text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] tracking-tight"
          >
            Stop clenching.
            <br />
            <span className="italic text-[#93C5FD]">Sleep restored.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 text-base lg:text-lg text-rb-text-2 max-w-xl leading-relaxed"
          >
            A focused, in-clinic programme to relieve teeth grinding, jaw
            clenching, morning headaches and TMJ tension — led by{" "}
            <span className="text-rb-text">Dr. Kartik Patel</span>. Send us a
            WhatsApp — we usually reply within the hour.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="btn-primary inline-flex items-center justify-center gap-2 group"
            >
              <MessageCircle size={18} strokeWidth={1.75} />
              Enquire on WhatsApp
            </a>
            <a
              href="#quiz"
              data-testid="hero-cta-quiz"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              <Sparkles size={16} strokeWidth={1.5} />
              Try the 30-sec quiz
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4 text-sm text-rb-text-2"
          >
            <div className="flex items-center gap-2">
              <Star size={16} strokeWidth={1.5} className="fill-[#93C5FD] text-[#93C5FD]" />
              <span><strong className="text-rb-text">5.0★</strong> Google rated</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} strokeWidth={1.5} className="text-[#93C5FD]" />
              <span><strong className="text-rb-text">15+ years</strong> clinical experience</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#93C5FD]" />
              <span>Same-week appointments often available</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-[#93C5FD]/15 blur-3xl" />
          <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full bg-[#3B82F6]/25 blur-3xl" />
          <div className="relative rounded-[28px] overflow-hidden border border-white/10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
            <img
              src="https://images.pexels.com/photos/6193198/pexels-photo-6193198.jpeg?auto=compress&cs=tinysrgb&w=1200&h=1400&fit=crop"
              alt="Woman holding her jaw, expression of pain from jaw clenching and an over-developed masseter"
              className="w-full h-[520px] object-cover"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
